/**
 * SkuTierPricePreview.jsx (iter 140)
 *
 * Compact 4-tier price preview for a single SKU. Renders as a small
 * icon button on Inventory rows; clicking opens a modal with the
 * 4 customer tiers, the auto-computed (or overridden) price, and the
 * live margin. Hides itself gracefully when the customer_pricing
 * feature is not active on the tenant.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Tag, Lock, Money, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n, currency = 'USD') => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  try {
    return Number(n).toLocaleString('en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
};

const marginCls = (pct) => {
  if (pct === null || pct === undefined) return 'bg-slate-100 text-slate-500';
  if (pct >= 30) return 'bg-emerald-50 text-emerald-700';
  if (pct >= 10) return 'bg-amber-50 text-amber-700';
  return 'bg-rose-50 text-rose-700';
};

export default function SkuTierPricePreview({ skuId, skuCode }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!skuId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/customer-pricing/sku/${skuId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      // 402 = feature not active → hide silently. 404 etc → surface.
      if (e?.response?.status === 402) setError('locked');
      else setError(e?.response?.data?.detail || 'Could not load tier prices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center justify-center w-7 h-7 text-muted-foreground hover:text-brand hover:bg-brand/5 border border-transparent hover:border-brand/30 transition"
        title="View 4-tier customer prices"
        data-testid={`tier-preview-btn-${skuCode}`}
      >
        <Tag size={14} weight="bold" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border border-border rounded-none max-w-xl" data-testid="tier-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Tag size={16} weight="fill" className="text-brand" />
              {skuCode} · Customer tier prices
            </DialogTitle>
          </DialogHeader>

          {loading && <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>}

          {error === 'locked' && (
            <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 space-y-3" data-testid="tier-preview-locked">
              <div className="flex items-center gap-2 font-bold"><Lock size={14} weight="fill" /> Customer Pricing add-on required</div>
              <p>Activate the <b>Custom Customer Price List ($149/mo)</b> add-on to use 4-tier pricing across all SKUs.</p>
              <Link to="/settings/billing/addons">
                <Button size="sm" className="bg-brand hover:bg-brand/90 text-white rounded-none" data-testid="tier-preview-upgrade">
                  View add-ons
                </Button>
              </Link>
            </div>
          )}

          {error && error !== 'locked' && (
            <div className="bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">{error}</div>
          )}

          {data && (
            <div className="space-y-3">
              <div className="bg-slate-50 border border-slate-200 px-3 py-2 text-xs grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Landed cost</span><div className="font-mono font-bold">{fmtMoney(data.landed_cost)}</div></div>
                <div><span className="text-muted-foreground">List price</span><div className="font-mono">{data.list_price ? fmtMoney(data.list_price) : '—'}</div></div>
              </div>
              <table className="w-full text-sm border border-border">
                <thead className="bg-muted text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left p-2">Tier</th>
                    <th className="text-left p-2">Mode</th>
                    <th className="text-right p-2">Price</th>
                    <th className="text-right p-2">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tiers.map((t) => (
                    <tr key={t.key} className="border-t border-border" data-testid={`tier-preview-row-${t.key}`}>
                      <td className="p-2 font-bold">{t.name}</td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {t.mode === 'discount' ? `−${t.value}%` : `+${t.value}%`} ({t.mode === 'discount' ? 'off list' : 'on cost'})
                      </td>
                      <td className="p-2 text-right tabular-nums font-mono font-bold">
                        {fmtMoney(t.price, t.currency)}
                        {t.is_override && <Lock size={10} weight="fill" className="inline ml-1 text-brand" />}
                      </td>
                      <td className={`p-2 text-right tabular-nums text-xs ${marginCls(t.margin_pct)}`}>
                        <Money size={10} weight="bold" className="inline mr-1" />
                        {t.margin_pct !== null ? `${t.margin_pct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end">
                <Link to="/settings/customer-pricing">
                  <Button variant="outline" size="sm" className="rounded-none" data-testid="tier-preview-open-editor">
                    Edit tiers · open matrix <ArrowSquareOut size={11} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
